import { fetchAdminUserLandingSites } from '@/api/admin';
import { Spinner } from '@/components/ui/spinner';
import type { ILandingSite } from '@/entities/landing.entity';
import LandingsEmptyState from '@/pages/landings/components/landings-empty-state';
import LandingsErrorState from '@/pages/landings/components/landings-error-state';
import { LayoutTemplateIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

interface LandingsSitesSubtabProps {
	profileId: number;
	fetchSites?: (profileId: number) => Promise<ILandingSite[]>;
}

export default function LandingsSitesSubtab({
	profileId,
	fetchSites = fetchAdminUserLandingSites,
}: LandingsSitesSubtabProps) {
	const [sites, setSites] = useState<ILandingSite[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	const load = useCallback(async (showSpinner = false) => {
		if (showSpinner) setLoading(true);
		setError(false);
		try {
			const data = await fetchSites(profileId);
			setSites(data);
		} catch {
			setError(true);
		} finally {
			if (showSpinner) setLoading(false);
		}
	}, [profileId, fetchSites]);

	useEffect(() => {
		void load(true);
	}, [load]);

	if (loading) {
		return (
			<div className='flex justify-center py-8'>
				<Spinner />
			</div>
		);
	}

	if (error) {
		return (
			<LandingsErrorState
				message='Лендинги недоступны. Проверьте сервис landing и LANDING_INTERNAL_SECRET.'
				onRetry={() => void load(true)}
			/>
		);
	}

	if (sites.length === 0) {
		return (
			<LandingsEmptyState
				icon={LayoutTemplateIcon}
				title='Сайтов нет'
				description='Пользователь ещё не создал сайты на доменах лендингов.'
			/>
		);
	}

	return (
		<div className='flex flex-col gap-3'>
			{sites.map(site => (
				<div key={site.id} className='rounded-xl border border-border bg-card p-3 shadow-sm'>
					<div className='flex items-start gap-3'>
						<div className='rounded-lg bg-chart-4/15 p-2 shrink-0'>
							<LayoutTemplateIcon className='text-chart-4' size={16} />
						</div>
						<div className='min-w-0 flex-1'>
							<p className='font-medium truncate'>{site.fullHostname}</p>
							<div className='mt-1.5 flex flex-wrap items-center gap-2'>
								<span className='inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground'>
									{site.templateSlug}
								</span>
								<span className='text-xs text-muted-foreground'>
									{new Date(site.createdAt).toLocaleDateString('ru-RU')}
								</span>
							</div>
						</div>
					</div>
				</div>
			))}
		</div>
	);
}
